<template>
  <ValidationProvider
    :class="['form__control', className]"
    :vid="vid"
    :rules="rules"
    :name="name"
    :mode="validationMode"
    :custom-messages="customMessages"
    tag="div"
    ref="provider"
    v-slot="{ errors, validated, invalid }"
  >
    <label :for="vid">
      <span :class="['label', labelClass]">
        {{ label }}
      </span>

      <div class="tooltip__wrap" v-if="tooltip">
        <v-tooltip top :min-width="tooltipMinWidth">
          <template v-slot:activator="{ on }">
            <div v-on="on">
              <div class="hidden-sm-and-down text-link text--size-small">
                {{ tooltip.label }}
              </div>
              <span class="hidden-md-and-up text-link">
                {{ tooltip.label }}
              </span>
            </div>
          </template>

          <span class="text-regular text--size-small">
            {{ tooltip.content }}
          </span>
        </v-tooltip>
      </div>
    </label>

    <v-text-field
      :id="vid"
      :placeholder="placeholder"
      background-color="white"
      :error-messages="errors[0] || externalErrorMsg"
      :append-icon="displayValidationIcon(errors, externalErrorMsg, validated)"
      :hide-details="hideDetails"
      :autocomplete="autocomplete"
      :class="[
        'input__control',
        { uppercase: isUppercase, 'field-invalid': invalid },
        { 'money-format': applyingCurrencyMask },
      ]"
      v-model="innerValue"
      v-mask="innerCurrencyMask"
      single-line
      outlined
      ref="input"
      :aria-label="ariaLabel"
      :hide-spin-buttons="hideSpinButtons"
      @blur="handleBlur"
      @keyup="handleKeyup"
      @paste="handlePaste"
      @keydown="handleKeyDown"
      :prefix="prefix"
      :dense="dense"
      :disabled="disabled"
      :readonly="readonly"
      :data-uiid="dataUiid"
      :height="height"
      :type="type"
      :inputmode="inputmode"
      :pattern="pattern"
      :min="inputmode === 'numeric' ? '0' : ''"
    >
      <template v-slot:message="{ message }">
        <span v-html="message"></span>
      </template>
    </v-text-field>
  </ValidationProvider>
</template>

<script>
import { currencyMask } from "@/common/utils";
import { ValidationProvider } from "vee-validate";

export default {
  name: "BaseInput",
  components: {
    ValidationProvider,
  },
  props: {
    vid: String,
    value: [String, Number],
    className: String,
    name: String,
    fieldName: String,
    label: String,
    labelClass: String,
    ariaLabel: String,
    externalErrorMsg: String,
    customMessages: Object,
    placeholder: String,
    hideDetails: { type: [Boolean, String] },
    isUppercase: Boolean,
    autocomplete: String,
    mask: String,
    tooltip: Object,
    rules: [Object, String],
    validationMode: {
      default: "eager",
      required: false,
    },
    prefix: String,
    dense: {
      type: Boolean,
      default: true,
    },
    hideValidationIcon: Boolean,
    disabled: Boolean,
    readonly: Boolean,
    moneyFormat: {
      type: Boolean,
      default: false,
    },
    dataUiid: {
      type: String,
      default: "",
    },
    height: [String, Number],
    type: {
      type: String,
      default: "text",
    },
    hideSpinButtons: {
      type: Boolean,
      default: true,
    },
    validationOnMount: {
      type: Boolean,
      default: true,
    },
    inputmode: {
      type: String,
    },
    pattern: {
      type: String,
    },
  },
  computed: {
    applyingCurrencyMask() {
      return !this.mask && this.moneyFormat;
    },
  },
  data: () => ({
    innerCurrencyMask: "",
    innerValue: "",
    tooltipMinWidth: 280,
    currencyMask,
    isEnteringNumber: false,
    isEnteringBackSpace: false,
    isPasting: false,
  }),
  watch: {
    // Handles internal model changes.
    innerValue(value) {
      if (this.applyingCurrencyMask && value) {
        this.innerCurrencyMask = this.createMask(value);
      }

      this.$emit("input", value, this.fieldName, this.$refs.provider);
    },
    // Handles external model changes.
    value(val) {
      if (val !== this.innerValue) {
        this.innerValue = val;
      }
    },
  },
  created() {
    this.innerCurrencyMask = this.applyingCurrencyMask
      ? this.createMask(this.value ?? "")
      : this.mask;
    if (this.value) {
      this.innerValue = this.value;
    }
  },
  mounted() {
    // trigger validate
    if (
      this.validationOnMount && // bypass validate on mount
      this.innerValue != null &&
      this.innerValue != "" // do not validate if the data is null or empty, will validate value like 0
    ) {
      this.$refs.input.updateValue();
      this.$refs.provider.validate();
    }

    // fix issue: tooltip width is not stable
    this.$nextTick(() => {
      this.tooltipMinWidth = this.$refs.input?.$el?.clientWidth;
    });
  },
  methods: {
    handleBlur(e) {
      this.$emit("blur", e.target.value, this.fieldName, this.$refs.provider);
    },
    handleKeyup(e) {
      // allowing number and pasting
      if (this.applyingCurrencyMask) {
        this.isEnteringNumber = !isNaN(+e.key) || e.key === "Enter";
        this.isEnteringBackSpace = e.key === "Backspace";
      }
    },
    handleKeyDown(e) {
      this.$emit("keydown", e);
    },
    handlePaste() {
      this.isPasting = true;
    },
    displayValidationIcon(errors, externalErrorMsg, validated) {
      if (this.hideValidationIcon) {
        return "";
      }
      return errors[0] || externalErrorMsg
        ? "mdi mdi-alert-circle"
        : validated
        ? "icon-check"
        : "";
    },
    createMask(value) {
      return this.currencyMask(value).filter((mask) => mask !== "[]");
    },
  },
};
</script>

<style lang="scss" scoped>
@media screen and (min-width: $grid-breakpoints-xs) {
  .tooltip__wrap {
    float: right;
    margin-top: 2px;
  }
}

@media screen and (max-width: ($grid-breakpoints-xs - 1)) {
  .tooltip__wrap {
    display: block;
  }
}

:deep(.v-text-field__prefix) {
  font-weight: bold;
}

:deep(.v-input__prepend-inner) {
  margin: auto 0 !important;
}

:deep(.v-input__append-inner) {
  margin: auto 0 !important;
  .v-icon.icon-check,
  .v-icon.mdi-alert-circle {
    width: 20px;
    height: 20px;
    font-size: 20px;
  }
}

.letter-spacing-4 {
  letter-spacing: 0.4px;
}

:deep(.v-text-field.v-text-field--enclosed) {
  .v-text-field__details {
    padding-left: 0;
    padding-right: 0;
    overflow: visible;
  }
}
</style>
